import { useQuery } from "@tanstack/react-query";

import { queryClient } from "@/api/core";
import { useAuth } from "@/contexts/auth/useAuth";
import { useApi } from "@/hooks";
import { instanceSchema, type Instance } from "@/schemas";

import { InstanceQueryKeys } from "../query-keys";

import type { AxiosError } from "axios";

interface Options {
  isEnabled?: boolean;
}

const defaultInstance = (): Instance => instanceSchema.parse({});

/** Cloudflare rate-limit / WAF pages are HTML; axios leaves them as strings. */
function isNonJsonResponse(data: unknown): data is string {
  return typeof data === "string";
}

function fallbackInstance(): Instance {
  const cached = queryClient.getQueryData<Instance>(InstanceQueryKeys.instance);
  return cached ?? defaultInstance();
}

function useInstance(options: Options = {}) {
  const { isEnabled = true } = options;

  const { isLoggedIn } = useAuth();

  const api = useApi();

  return useQuery<Instance, AxiosError>({
    queryKey: InstanceQueryKeys.instance,
    useErrorBoundary: false,
    enabled: isLoggedIn || isEnabled,
    queryFn: async () => {
      const { data } = await api.get("/api/v1/instance");

      if (isNonJsonResponse(data)) {
        return fallbackInstance();
      }

      const parsed = instanceSchema.safeParse(data);

      if (parsed.success) {
        return parsed.data;
      }

      return fallbackInstance();
    },
  });
}

export default useInstance;
